import type { ActivityType, PlanType } from '@app/common';
import type { Language } from '../constants/language';
import type { Department, DifficultyLevel, WizardStep } from '../types/wizard';
import { en } from './en';
import { bn } from './bn';

/** Every key the app can translate, derived from the English dictionary. */
export type TranslationKey = keyof typeof en;

/**
 * Plan names for display. `PLAN_NAMES` in `@app/common` is shared with the backend
 * and is English-only by design, so the UI resolves its own labels — reusing the
 * pricing keys rather than defining a second set that could drift from them.
 */
export const PLAN_NAME_KEYS: Record<PlanType, TranslationKey> = {
  free_trial: 'pricing.planFreeTrial',
  pro_trial: 'pricing.planProTrial',
  pro: 'pricing.planPro',
  institution: 'pricing.planInstitution',
};

/**
 * Labels for values that live in `types/wizard.ts` as data. Those tables also hold
 * a `banglaName`/`banglaLabel` used to build the *paper*, which is a different
 * concern — a Bengali UI must not change what gets sent to the model, so display
 * labels resolve through these keys instead of reading those fields.
 */
/**
 * Human labels for audit-log event types. Exhaustive by type, so adding a value
 * to `ActivityType` in `@app/common` is a compile error until it is labelled in
 * both languages — the same guarantee `bn.ts` gives for keys.
 */
export const ACTIVITY_TYPE_KEYS: Record<ActivityType, TranslationKey> = {
  user_signed_up: 'activity.user_signed_up',
  user_signed_in: 'activity.user_signed_in',
  question_generated: 'activity.question_generated',
  generation_blocked: 'activity.generation_blocked',
  generation_failed: 'activity.generation_failed',
  payment_initiated: 'activity.payment_initiated',
  payment_completed: 'activity.payment_completed',
  payment_failed: 'activity.payment_failed',
  subscription_activated: 'activity.subscription_activated',
  subscription_cancelled: 'activity.subscription_cancelled',
  user_banned: 'activity.user_banned',
  user_unbanned: 'activity.user_unbanned',
};

export const DEPARTMENT_NAME_KEYS: Record<Department, TranslationKey> = {
  science: 'dept.science',
  commerce: 'dept.commerce',
  arts: 'dept.arts',
};

export const DIFFICULTY_LABEL_KEYS: Record<DifficultyLevel, TranslationKey> = {
  easy: 'wizard.difficultyEasy',
  medium: 'wizard.difficultyMedium',
  hard: 'wizard.difficultyHard',
  mixed: 'wizard.difficultyMixed',
};

export const DIFFICULTY_DESCRIPTION_KEYS: Record<DifficultyLevel, TranslationKey> = {
  easy: 'wizard.difficultyEasyDesc',
  medium: 'wizard.difficultyMediumDesc',
  hard: 'wizard.difficultyHardDesc',
  mixed: 'wizard.difficultyMixedDesc',
};

export const STEP_LABEL_KEYS: Record<WizardStep, TranslationKey> = {
  class: 'wizard.stepClass',
  version: 'wizard.stepVersion',
  department: 'wizard.stepDepartment',
  subject: 'wizard.stepSubject',
  paper: 'wizard.stepPaper',
  chapter: 'wizard.stepChapter',
  examType: 'wizard.stepExamType',
  questions: 'wizard.stepQuestions',
  review: 'wizard.stepReview',
  generate: 'wizard.stepGenerate',
};

/** Values substituted into `{placeholder}` slots. */
export type TranslationParams = Record<string, string | number>;

/** The shape `useTranslation().t` has, for helpers that take it as an argument. */
export type TranslateFn = (key: TranslationKey, params?: TranslationParams) => string;

const DICTIONARIES: Record<Language, Record<TranslationKey, string>> = { en, bn };

/**
 * Resolves a key in the given language, substituting any `{placeholder}` slots.
 *
 * There is no missing-key fallback because there cannot be a missing key: `bn` is
 * typed against `TranslationKey`, so an incomplete dictionary fails the build.
 */
export function translate(
  language: Language,
  key: TranslationKey,
  params?: TranslationParams
): string {
  const template = DICTIONARIES[language][key];
  if (!params) return template;

  return Object.entries(params).reduce(
    (text, [name, value]) => text.split(`{${name}}`).join(String(value)),
    template
  );
}

export { en, bn };
